import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, CheckCircle, XCircle, Trash2, AlertCircle, ChevronDown, ChevronRight, Building2, UserCog, ArrowLeft, Search, Wrench, User, Package } from "lucide-react";
import { Input } from "@/components/ui/input";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPanel = ({ user }) => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [pendingRoles, setPendingRoles] = useState({});
  const [pendingDistributors, setPendingDistributors] = useState({});
  const [distributors, setDistributors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [expandedSections, setExpandedSections] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  
  const isDistributorAdmin = user?.role === 'distributor_admin';
  const userDistributor = user?.distributor;

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchUsers = async () => {
    try {
      const [allUsersRes, pendingUsersRes] = await Promise.all([
        axios.get(`${API}/admin/users`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/users/pending`, { headers: getAuthHeaders() })
      ]);
      
      let filteredUsers = allUsersRes.data;
      let filteredPending = pendingUsersRes.data;
      
      if (isDistributorAdmin && userDistributor) {
        // Distributor admins see:
        // 1. Users in their distributor group
        // 2. Installers who selected them as preferred distributor (read-only view only)
        filteredUsers = allUsersRes.data.filter(u => 
          u.distributor === userDistributor || 
          (u.role === 'installer' && u.preferred_distributor === userDistributor)
        );
        // Pending users: ONLY users registering for their distributor
        // NOT installers - they can only view installers, not approve them
        filteredPending = pendingUsersRes.data.filter(u => 
          u.distributor === userDistributor
        );
      }
      
      setUsers(filteredUsers);
      setPendingUsers(filteredPending);
      setError("");
    } catch (err) {
      setError("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  const fetchDistributors = async () => {
    try {
      const response = await axios.get(`${API}/distributors`);
      setDistributors(response.data.distributors);
    } catch (err) {
      console.error("Failed to fetch distributors:", err);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (user && token) {
      fetchUsers();
      fetchDistributors();
    } else if (!token) {
      setError("Authentication required. Please log in again.");
      setLoading(false);
    }
  }, [user]);

  // Auto-expand sections that have pending users and initialize defaults
  useEffect(() => {
    if (pendingUsers.length > 0) {
      setExpandedSections(prev => ({ ...prev, pending: true }));
      
      // Pre-initialize pendingRoles and pendingDistributors to match UI defaults
      const initialRoles = {};
      const initialDistributors = {};
      
      pendingUsers.forEach(u => {
        // Set default role based on portal
        if (u.portal === 'amazon') {
          initialRoles[u.email] = 'amazon';
        } else if (u.portal === 'securest') {
          initialRoles[u.email] = 'securest';
        } else {
          initialRoles[u.email] = 'installer';
        }
        
        // Set default distributor
        initialDistributors[u.email] = u.preferred_distributor || u.distributor || 'none';
      });
      
      // Only set if not already set (don't override user selections)
      setPendingRoles(prev => ({ ...initialRoles, ...prev }));
      setPendingDistributors(prev => ({ ...initialDistributors, ...prev }));
    }
  }, [pendingUsers]);

  const handleUpdateUser = async (email, role, status, distributor = null, pricingTier = undefined) => {
    setError("");
    setSuccess("");
    try {
      const updateData = { role, status };
      if (distributor !== null) {
        updateData.distributor = distributor;
      }
      if (pricingTier !== undefined) {
        updateData.pricing_tier = pricingTier;
      }
      
      await axios.put(
        `${API}/admin/users/${email}`,
        updateData,
        { headers: getAuthHeaders() }
      );
      setSuccess(`User ${email} updated successfully`);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to update user");
    }
  };

  const handleApproveUser = async (email) => {
    const pendingUser = pendingUsers.find(u => u.email === email);
    
    if (isDistributorAdmin) {
      // Distributor admins approve users for their distributor
      // Default role is "distributor", can also choose "subdistributor"
      const selectedRole = pendingRoles[email] || 'distributor';
      await handleUpdateUser(email, selectedRole, 'active', userDistributor);
    } else {
      // Full admins can set role and distributor
      // Use the explicitly selected role from dropdown, or use the same defaults as the UI
      let selectedRole = pendingRoles[email];
      
      if (!selectedRole) {
        // Match the dropdown default based on portal type
        if (pendingUser?.portal === 'amazon') {
          selectedRole = 'amazon';
        } else if (pendingUser?.portal === 'securest') {
          selectedRole = 'securest';
        } else {
          // Default to 'installer' - matching the dropdown default value
          selectedRole = 'installer';
        }
      }
      
      // Determine distributor assignment - use the dropdown value or fall back to user's preferred/existing
      let selectedDistributor = pendingDistributors[email];
      
      if (selectedDistributor === undefined) {
        // No explicit selection in dropdown - use the same default as shown in UI
        selectedDistributor = pendingUser?.preferred_distributor || pendingUser?.distributor || 'none';
      }
      
      // For ZKTeco staff roles, don't assign a distributor
      const finalDistributor = (selectedRole === 'zkteco' || selectedRole === 'zkteco_admin' || selectedRole === 'admin') 
        ? '' 
        : (selectedDistributor === 'none' ? '' : selectedDistributor);
      
      await handleUpdateUser(email, selectedRole, 'active', finalDistributor);
    }
  };

  const handleRejectUser = async (email) => {
    await handleUpdateUser(email, 'enduser', 'inactive');
  };

  const handleDeleteUser = async (email) => {
    if (!window.confirm(`Are you sure you want to delete ${email}?`)) return;
    
    try {
      await axios.delete(`${API}/admin/users/${email}`, { headers: getAuthHeaders() });
      setSuccess(`User ${email} deleted successfully`);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to delete user");
    }
  };

  const getRoleBadge = (role) => {
    const colors = {
      admin: "bg-purple-100 text-purple-800 border-purple-200",
      zkteco_admin: "bg-pink-100 text-pink-800 border-pink-200",
      distributor: "bg-blue-100 text-blue-800 border-blue-200",
      distributor_admin: "bg-indigo-100 text-indigo-800 border-indigo-200",
      installer: "bg-green-100 text-green-800 border-green-200",
      enduser: "bg-slate-100 text-slate-800 border-slate-200",
      zkteco: "bg-orange-100 text-orange-800 border-orange-200",
      subdistributor: "bg-cyan-100 text-cyan-800 border-cyan-200",
      amazon: "bg-amber-100 text-amber-800 border-amber-200",
      amazon_admin: "bg-orange-100 text-orange-800 border-orange-200",
      securest: "bg-teal-100 text-teal-800 border-teal-200"
    };
    return colors[role] || colors.enduser;
  };

  const getRoleDisplayName = (role) => {
    const names = {
      admin: "Admin",
      zkteco_admin: "ZKTeco Admin",
      distributor: "Distributor",
      distributor_admin: "Distributor Admin",
      installer: "Installer",
      enduser: "End User",
      zkteco: "ZKTeco",
      subdistributor: "Sub Distributor",
      amazon: "Amazon User",
      amazon_admin: "Amazon Admin",
      securest: "Securest Only"
    };
    return names[role] || role;
  };

  const getStatusBadge = (status) => {
    const colors = {
      active: "bg-green-100 text-green-800",
      pending: "bg-yellow-100 text-yellow-800",
      inactive: "bg-red-100 text-red-800"
    };
    return <Badge className={colors[status] || colors.inactive}>{status}</Badge>;
  };

  const getLastOnlineText = (lastLogin) => {
    if (!lastLogin) return "Never";
    const now = new Date();
    const loginDate = new Date(lastLogin);
    const diffMs = now - loginDate;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 5) return "Online";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return loginDate.toLocaleDateString('en-GB');
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  // Group users by category
  const groupedUsers = () => {
    const groups = {
      zkteco_staff: [],
      installers: [],
      endusers: [],
      amazon_users: [],
      amazon_admins: [],
      unassigned: []
    };
    
    // Initialize groups for each distributor
    distributors.forEach(dist => {
      groups[dist] = [];
    });

    // Filter by search term
    const filteredUsers = users.filter(u => {
      if (!searchTerm) return true;
      const search = searchTerm.toLowerCase();
      return (
        u.email?.toLowerCase().includes(search) ||
        u.company_name?.toLowerCase().includes(search) ||
        u.first_name?.toLowerCase().includes(search) ||
        u.last_name?.toLowerCase().includes(search)
      );
    });

    // Categorize users
    filteredUsers.forEach(u => {
      if (u.role === 'amazon') {
        // Amazon users - only visible to admin and zkteco_admin
        if (!isDistributorAdmin) {
          groups.amazon_users.push(u);
        }
      } else if (u.role === 'amazon_admin') {
        // Amazon admins - only visible to admin and zkteco_admin
        if (!isDistributorAdmin) {
          groups.amazon_admins.push(u);
        }
      } else if (u.role === 'admin' || u.role === 'zkteco_admin' || u.role === 'zkteco') {
        // ZKTeco staff - only visible to full admins
        if (!isDistributorAdmin) {
          groups.zkteco_staff.push(u);
        }
      } else if (u.role === 'distributor' || u.role === 'distributor_admin' || u.role === 'subdistributor') {
        // Distributor users - group by their distributor
        if (u.distributor && groups[u.distributor] !== undefined) {
          // For distributor admins, only show their own distributor's users
          if (isDistributorAdmin) {
            if (u.distributor === userDistributor) {
              groups[u.distributor].push(u);
            }
          } else {
            groups[u.distributor].push(u);
          }
        } else if (!isDistributorAdmin) {
          // Unassigned distributor users - only visible to full admins
          groups.unassigned.push(u);
        }
      } else if (u.role === 'installer') {
        // Installers - for distributor admins, only show those with their distributor as preferred
        if (isDistributorAdmin) {
          if (u.preferred_distributor === userDistributor) {
            groups.installers.push(u);
          }
        } else {
          // Full admins see all installers
          // Installers with preferred_distributor = "ZKTeco" are managed by ZKTeco
          groups.installers.push(u);
        }
      } else if (u.role === 'enduser') {
        // End users - only visible to full admins (no preferred distributor)
        if (!isDistributorAdmin) {
          groups.endusers.push(u);
        }
      } else {
        // Other unassigned users - only visible to full admins
        if (!isDistributorAdmin) {
          groups.unassigned.push(u);
        }
      }
    });

    return groups;
  };

  // Simple read-only row for distributor admins
  const ReadOnlyUserRow = ({ u }) => (
    <tr className="border-b border-slate-100 hover:bg-slate-50">
      <td className="py-2 px-3">
        <div>
          <p className="font-medium text-slate-900 text-sm">{u.email}</p>
          <p className="text-xs text-slate-500">{u.company_name || u.first_name && u.last_name ? `${u.first_name} ${u.last_name}` : '-'}</p>
        </div>
      </td>
      <td className="py-2 px-3">
        <Badge className={getRoleBadge(u.role)}>{getRoleDisplayName(u.role)}</Badge>
      </td>
      <td className="py-2 px-3 text-slate-500 text-sm">
        {u.status === 'active' ? (
          <span className="text-green-600">Active</span>
        ) : (
          <span className="text-slate-400">{u.status}</span>
        )}
      </td>
      <td className="py-2 px-3 text-slate-500 text-xs">
        {u.last_online ? (
          new Date(u.last_online).toLocaleDateString() === new Date().toLocaleDateString() 
            ? 'Online' 
            : new Date(u.last_online).toLocaleDateString('en-GB')
        ) : 'Never'}
      </td>
    </tr>
  );

  // Simplified section for distributor admins (read-only)
  const ReadOnlyUserSection = ({ title, icon: Icon, users, sectionId, color }) => {
    const isExpanded = expandedSections[sectionId] !== false;
    
    return (
      <Card className="mb-4">
        <button
          onClick={() => toggleSection(sectionId)}
          className={`w-full flex items-center justify-between p-4 hover:bg-${color}-50 transition-colors`}
        >
          <div className="flex items-center gap-3">
            <Icon className={`h-5 w-5 text-${color}-600`} />
            <span className="font-semibold text-slate-800">{title}</span>
            <Badge variant="outline" className={`bg-${color}-50 text-${color}-800`}>{users.length}</Badge>
          </div>
          {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
        {isExpanded && users.length > 0 && (
          <CardContent className="p-0">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left py-2 px-3 font-semibold text-slate-600 text-xs">User</th>
                  <th className="text-left py-2 px-3 font-semibold text-slate-600 text-xs">Role</th>
                  <th className="text-left py-2 px-3 font-semibold text-slate-600 text-xs">Status</th>
                  <th className="text-left py-2 px-3 font-semibold text-slate-600 text-xs">Last Online</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => <ReadOnlyUserRow key={u.email} u={u} />)}
              </tbody>
            </table>
          </CardContent>
        )}
      </Card>
    );
  };
  const UserRow = ({ u }) => {
    // Admin can edit all users except other admins (safety measure)
    // Current user cannot demote themselves from admin
    const isCurrentUser = u.email === user?.email;
    const canEditRole = user?.role === 'admin' 
      ? (u.role !== 'admin' || !isCurrentUser) // Admin can edit anyone except demoting themselves
      : user?.role === 'zkteco_admin'
        ? (u.role !== 'admin' && u.role !== 'zkteco_admin') // ZKTeco admin can't edit admins
        : false; // Distributor admins handled separately
    
    const canEditDistributor = user?.role === 'admin' || user?.role === 'zkteco_admin';
    
    return (
    <tr className="border-b border-slate-100 hover:bg-slate-50">
      <td className="py-2 px-3">
        <div>
          <p className="font-medium text-slate-900 text-sm">{u.email}</p>
          <p className="text-xs text-slate-500">{u.company_name || '-'}</p>
        </div>
      </td>
      <td className="py-2 px-3">
        {!canEditRole || isDistributorAdmin ? (
          // Show read-only badge
          <Badge className={getRoleBadge(u.role)}>{getRoleDisplayName(u.role)}</Badge>
        ) : (
          <Select
            value={u.role}
            onValueChange={(role) => {
              // When changing to ZKTeco staff role, clear distributor
              const newDistributor = (role === 'zkteco' || role === 'zkteco_admin' || role === 'admin') 
                ? '' 
                : u.distributor;
              handleUpdateUser(u.email, role, u.status, newDistributor);
            }}
          >
            <SelectTrigger className="w-32 h-7 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {user?.role === 'admin' && (
                <>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="zkteco_admin">ZKTeco Admin</SelectItem>
                </>
              )}
              <SelectItem value="zkteco">ZKTeco</SelectItem>
              <SelectItem value="distributor">Distributor</SelectItem>
              <SelectItem value="distributor_admin">Distributor Admin</SelectItem>
              <SelectItem value="subdistributor">Sub Distributor</SelectItem>
              <SelectItem value="installer">Installer</SelectItem>
              <SelectItem value="enduser">End User</SelectItem>
              {(user?.role === 'admin' || user?.role === 'zkteco_admin') && (
                <>
                  <SelectItem value="amazon">Amazon User</SelectItem>
                  <SelectItem value="amazon_admin">Amazon Admin</SelectItem>
                  <SelectItem value="securest">Securest Only</SelectItem>
                </>
              )}
            </SelectContent>
          </Select>
        )}
      </td>
      <td className="py-2 px-3">
        {!canEditDistributor || isDistributorAdmin ? (
          <span className="text-slate-500 text-xs">{u.distributor || '-'}</span>
        ) : (
          <Select
            value={u.distributor || "none"}
            onValueChange={(distributor) => handleUpdateUser(u.email, u.role, u.status, distributor === "none" ? "" : distributor)}
          >
            <SelectTrigger className="w-32 h-7 text-xs">
              <SelectValue placeholder="Select..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              <SelectItem value="ZKTeco">ZKTeco</SelectItem>
              {distributors.map((dist) => (
                <SelectItem key={dist} value={dist}>{dist}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </td>
      {!isDistributorAdmin && (
        <td className="py-2 px-3">
          {(u.role === 'distributor' || u.role === 'distributor_admin') ? (
            <Select
              value={u.pricing_tier || "org_default"}
              onValueChange={(pricingTier) => handleUpdateUser(u.email, u.role, u.status, u.distributor, pricingTier === "org_default" ? "" : pricingTier)}
            >
              <SelectTrigger className="w-36 h-7 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="org_default">Use Org Default</SelectItem>
                <SelectItem value="main_channel">Main - Channel</SelectItem>
                <SelectItem value="main_sub_distribution">Main - Sub Dist</SelectItem>
                <SelectItem value="sub_distribution">Sub Distribution</SelectItem>
              </SelectContent>
            </Select>
          ) : u.role === 'installer' ? (
            <Select
              value={u.pricing_tier || "standard"}
              onValueChange={(pricingTier) => handleUpdateUser(u.email, u.role, u.status, u.distributor, pricingTier === "standard" ? "" : pricingTier)}
            >
              <SelectTrigger className="w-36 h-7 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="standard">Standard Installer</SelectItem>
                <SelectItem value="key_installer">Key Installer</SelectItem>
              </SelectContent>
            </Select>
          ) : (
            <span className="text-slate-400 text-xs">-</span>
          )}
        </td>
      )}
      <td className="py-2 px-3">
        {!canEditRole || isDistributorAdmin ? (
          getStatusBadge(u.status)
        ) : (
          <Select
            value={u.status}
            onValueChange={(status) => handleUpdateUser(u.email, u.role, status, u.distributor)}
          >
            <SelectTrigger className="w-24 h-7 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
        )}
      </td>
      <td className="py-2 px-3 text-slate-500 text-xs">
        {getLastOnlineText(u.last_online || u.last_login)}
      </td>
      <td className="py-2 px-3 text-right">
        {canEditRole && !isDistributorAdmin && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleDeleteUser(u.email)}
            className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </td>
    </tr>
  );
  };

  const UserSection = ({ title, icon: Icon, users, sectionId, color = "blue", badgeCount }) => {
    const isExpanded = expandedSections[sectionId];
    const count = badgeCount !== undefined ? badgeCount : users.length;
    
    if (count === 0 && sectionId !== 'pending') return null;

    return (
      <Card className="mb-4">
        <div 
          className={`flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50 rounded-t-lg ${isExpanded ? 'border-b' : ''}`}
          onClick={() => toggleSection(sectionId)}
        >
          <div className="flex items-center gap-3">
            {isExpanded ? (
              <ChevronDown className={`h-5 w-5 text-${color}-600`} />
            ) : (
              <ChevronRight className={`h-5 w-5 text-${color}-600`} />
            )}
            <Icon className={`h-5 w-5 text-${color}-600`} />
            <h3 className="font-semibold text-slate-800">{title}</h3>
            <Badge className={`bg-${color}-100 text-${color}-800`}>{count}</Badge>
          </div>
        </div>
        
        {isExpanded && users.length > 0 && (
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left py-2 px-3 font-semibold text-slate-600">User</th>
                    <th className="text-left py-2 px-3 font-semibold text-slate-600">Role</th>
                    <th className="text-left py-2 px-3 font-semibold text-slate-600">Distributor</th>
                    {!isDistributorAdmin && (
                      <th className="text-left py-2 px-3 font-semibold text-slate-600">Pricing Tier</th>
                    )}
                    <th className="text-left py-2 px-3 font-semibold text-slate-600">Status</th>
                    <th className="text-left py-2 px-3 font-semibold text-slate-600">Last Online</th>
                    <th className="text-right py-2 px-3 font-semibold text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => <UserRow key={u.email} u={u} />)}
                </tbody>
              </table>
            </div>
          </CardContent>
        )}
        
        {isExpanded && users.length === 0 && (
          <CardContent>
            <p className="text-slate-500 text-sm text-center py-4">No users in this section</p>
          </CardContent>
        )}
      </Card>
    );
  };

  const PendingSection = () => {
    if (pendingUsers.length === 0) return null;
    
    const isExpanded = expandedSections['pending'];

    return (
      <Card className="mb-4 border-yellow-200 bg-yellow-50/50">
        <div 
          className={`flex items-center justify-between p-4 cursor-pointer hover:bg-yellow-100/50 rounded-t-lg ${isExpanded ? 'border-b border-yellow-200' : ''}`}
          onClick={() => toggleSection('pending')}
        >
          <div className="flex items-center gap-3">
            {isExpanded ? (
              <ChevronDown className="h-5 w-5 text-yellow-600" />
            ) : (
              <ChevronRight className="h-5 w-5 text-yellow-600" />
            )}
            <AlertCircle className="h-5 w-5 text-yellow-600" />
            <h3 className="font-semibold text-yellow-800">Pending Approvals</h3>
            <Badge className="bg-yellow-200 text-yellow-800">{pendingUsers.length}</Badge>
          </div>
        </div>
        
        {isExpanded && (
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-yellow-100/50">
                  <tr>
                    <th className="text-left py-2 px-3 font-semibold text-yellow-800">User</th>
                    <th className="text-left py-2 px-3 font-semibold text-yellow-800">Company</th>
                    <th className="text-left py-2 px-3 font-semibold text-yellow-800">Distributor</th>
                    <th className="text-left py-2 px-3 font-semibold text-yellow-800">Assign Role</th>
                    {!isDistributorAdmin && (
                      <th className="text-left py-2 px-3 font-semibold text-yellow-800">Assign Distributor</th>
                    )}
                    <th className="text-left py-2 px-3 font-semibold text-yellow-800">Registered</th>
                    <th className="text-right py-2 px-3 font-semibold text-yellow-800">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingUsers.map((u) => (
                    <tr key={u.email} className={`border-b border-yellow-100 hover:bg-yellow-50 ${u.portal === 'amazon' ? 'bg-amber-50/50' : ''}`}>
                      <td className="py-2 px-3">
                        <div className="flex items-center gap-2">
                          <div>
                            <p className="font-medium text-slate-900 text-sm">{u.email}</p>
                            <p className="text-xs text-slate-500">{u.first_name} {u.last_name}</p>
                          </div>
                          {u.portal === 'amazon' && (
                            <Badge className="bg-amber-100 text-amber-700 text-xs">Amazon</Badge>
                          )}
                          {u.portal === 'securest' && (
                            <Badge className="bg-teal-100 text-teal-700 text-xs" data-testid={`pending-securest-badge-${u.email}`}>Securest invite</Badge>
                          )}
                        </div>
                      </td>
                      <td className="py-2 px-3 text-slate-600 text-sm">
                        {u.portal === 'amazon' ? (u.position || '-') : (u.company_name || '-')}
                      </td>
                      <td className="py-2 px-3">
                        <p className="text-slate-600 text-sm">
                          {u.portal === 'amazon' ? (u.site_postcode || '-') : (u.distributor || u.preferred_distributor || '-')}
                        </p>
                      </td>
                      <td className="py-2 px-3">
                        {u.portal === 'amazon' ? (
                          // Amazon users - can only be amazon or amazon_admin
                          <Select
                            value={pendingRoles[u.email] || "amazon"}
                            onValueChange={(role) => setPendingRoles({...pendingRoles, [u.email]: role})}
                          >
                            <SelectTrigger className="w-32 h-7 text-xs">
                              <SelectValue placeholder="Amazon User" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="amazon">Amazon User</SelectItem>
                              <SelectItem value="amazon_admin">Amazon Admin</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : isDistributorAdmin ? (
                          // Distributor admins - only approve distributor users
                          <Select
                            value={pendingRoles[u.email] || "distributor"}
                            onValueChange={(role) => setPendingRoles({...pendingRoles, [u.email]: role})}
                          >
                            <SelectTrigger className="w-32 h-7 text-xs">
                              <SelectValue placeholder="Distributor" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="distributor">Distributor</SelectItem>
                              <SelectItem value="subdistributor">Sub Distributor</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          // Full admins can assign any role
                          <Select
                            value={pendingRoles[u.email] || "installer"}
                            onValueChange={(role) => setPendingRoles({...pendingRoles, [u.email]: role})}
                          >
                            <SelectTrigger className="w-32 h-7 text-xs">
                              <SelectValue placeholder="Select role" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="distributor">Distributor</SelectItem>
                              <SelectItem value="distributor_admin">Distributor Admin</SelectItem>
                              <SelectItem value="subdistributor">Sub Distributor</SelectItem>
                              <SelectItem value="installer">Installer</SelectItem>
                              <SelectItem value="enduser">End User</SelectItem>
                              <SelectItem value="zkteco">ZKTeco</SelectItem>
                              {(user?.role === 'admin' || user?.role === 'zkteco_admin') && (
                                <>
                                  <SelectItem value="amazon">Amazon User</SelectItem>
                                  <SelectItem value="amazon_admin">Amazon Admin</SelectItem>
                                  <SelectItem value="securest">Securest Only</SelectItem>
                                </>
                              )}
                            </SelectContent>
                          </Select>
                        )}
                      </td>
                      {!isDistributorAdmin && (
                        <td className="py-2 px-3">
                          {u.portal === 'amazon' ? (
                            <span className="text-slate-400 text-xs">N/A</span>
                          ) : (
                            <Select
                              value={pendingDistributors[u.email] || u.preferred_distributor || u.distributor || "none"}
                              onValueChange={(dist) => setPendingDistributors({...pendingDistributors, [u.email]: dist})}
                            >
                              <SelectTrigger className="w-32 h-7 text-xs">
                                <SelectValue placeholder="Select..." />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">None</SelectItem>
                                <SelectItem value="ZKTeco">ZKTeco</SelectItem>
                                {distributors.map((dist) => (
                                  <SelectItem key={dist} value={dist}>{dist}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </td>
                      )}
                      <td className="py-2 px-3 text-slate-500 text-xs">
                        {new Date(u.created_at).toLocaleDateString('en-GB')}
                      </td>
                      <td className="py-2 px-3 text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            size="sm"
                            onClick={() => handleApproveUser(u.email)}
                            className="h-7 bg-green-600 hover:bg-green-700 text-xs"
                          >
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleRejectUser(u.email)}
                            className="h-7 text-xs"
                          >
                            <XCircle className="h-3 w-3 mr-1" />
                            Reject
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        )}
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const groups = groupedUsers();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(-1)}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-800">
                {isDistributorAdmin ? `${userDistributor} - User Management` : 'User Management'}
              </h1>
              <p className="text-slate-500 text-sm">
                {isDistributorAdmin 
                  ? `${(groups[userDistributor]?.length || 0)} users • ${groups.installers?.length || 0} assigned installers • ${pendingUsers.length} pending`
                  : `${users.length} total users • ${pendingUsers.length} pending approval`
                }
              </p>
            </div>
          </div>
          
          {/* Search */}
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {success && (
          <Alert className="mb-4 bg-green-50 border-green-200">
            <AlertDescription className="text-green-800">{success}</AlertDescription>
          </Alert>
        )}

        {/* Pending Approvals - Show for distributor admins too */}
        <PendingSection />

        {/* ZKTeco Staff Section */}
        {!isDistributorAdmin && (
          <UserSection
            title="ZKTeco Staff"
            icon={UserCog}
            users={groups.zkteco_staff}
            sectionId="zkteco_staff"
            color="purple"
          />
        )}

        {/* Distributor Sections */}
        {isDistributorAdmin ? (
          // Distributor admin sees their own distributor section (can edit except pricing)
          userDistributor && (
            <UserSection
              key={userDistributor}
              title={userDistributor}
              icon={Building2}
              users={groups[userDistributor] || []}
              sectionId={userDistributor}
              color="blue"
            />
          )
        ) : (
          // Full admins see all distributors
          distributors.map((dist) => (
            <UserSection
              key={dist}
              title={dist}
              icon={Building2}
              users={groups[dist] || []}
              sectionId={dist}
              color="blue"
            />
          ))
        )}

        {/* Installers Section - Distributor admins see installers who selected them */}
        {isDistributorAdmin ? (
          <ReadOnlyUserSection
            title="My Assigned Installers"
            icon={Wrench}
            users={groups.installers}
            sectionId="installers"
            color="green"
          />
        ) : (
          <UserSection
            title="Installers"
            icon={Wrench}
            users={groups.installers}
            sectionId="installers"
            color="green"
          />
        )}

        {/* End Users Section - Only visible to full admins */}
        {!isDistributorAdmin && (
          <UserSection
            title="End Users"
            icon={User}
            users={groups.endusers}
            sectionId="endusers"
            color="slate"
          />
        )}

        {/* Amazon Section - Only visible to admin and zkteco_admin */}
        {!isDistributorAdmin && (
          <Card className="border-amber-200">
            <CardHeader 
              className="py-3 cursor-pointer hover:bg-amber-50/50 transition-colors"
              onClick={() => toggleSection('amazon')}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-amber-100">
                    <Package className="h-5 w-5 text-amber-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Amazon</CardTitle>
                    <p className="text-xs text-slate-500">{groups.amazon_users.length + groups.amazon_admins.length} users</p>
                  </div>
                </div>
                {expandedSections.amazon ? (
                  <ChevronDown className="h-5 w-5 text-slate-400" />
                ) : (
                  <ChevronRight className="h-5 w-5 text-slate-400" />
                )}
              </div>
            </CardHeader>
            {expandedSections.amazon && (
              <CardContent className="pt-0 space-y-4">
                {/* Amazon Users Sub-section */}
                <div className="border rounded-lg p-3 bg-amber-50/30">
                  <h4 className="font-medium text-sm text-amber-800 mb-2">Amazon Users ({groups.amazon_users.length})</h4>
                  {groups.amazon_users.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-left">
                        <thead>
                          <tr className="text-xs text-slate-500 border-b">
                            <th className="py-2 px-3 font-medium">Email</th>
                            <th className="py-2 px-3 font-medium">Position</th>
                            <th className="py-2 px-3 font-medium">Site</th>
                            <th className="py-2 px-3 font-medium">Role</th>
                            <th className="py-2 px-3 font-medium">Status</th>
                            <th className="py-2 px-3 font-medium">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {groups.amazon_users.map((u) => (
                            <tr key={u.email} className="border-b border-amber-100 hover:bg-amber-50/50">
                              <td className="py-2 px-3">
                                <p className="font-medium text-slate-900 text-sm">{u.email}</p>
                                <p className="text-xs text-slate-500">{u.first_name} {u.last_name}</p>
                              </td>
                              <td className="py-2 px-3 text-slate-600 text-sm">{u.position || '-'}</td>
                              <td className="py-2 px-3 text-slate-600 text-sm">{u.site_postcode || '-'}</td>
                              <td className="py-2 px-3">
                                <Select
                                  value={u.role}
                                  onValueChange={(role) => {
                                    const newDistributor = (role === 'zkteco' || role === 'zkteco_admin' || role === 'admin') 
                                      ? '' 
                                      : null;
                                    handleUpdateUser(u.email, role, u.status, newDistributor);
                                  }}
                                >
                                  <SelectTrigger className="w-32 h-7 text-xs">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {user?.role === 'admin' && (
                                      <>
                                        <SelectItem value="admin">Admin</SelectItem>
                                        <SelectItem value="zkteco_admin">ZKTeco Admin</SelectItem>
                                      </>
                                    )}
                                    <SelectItem value="zkteco">ZKTeco</SelectItem>
                                    <SelectItem value="distributor">Distributor</SelectItem>
                                    <SelectItem value="distributor_admin">Distributor Admin</SelectItem>
                                    <SelectItem value="subdistributor">Sub Distributor</SelectItem>
                                    <SelectItem value="installer">Installer</SelectItem>
                                    <SelectItem value="enduser">End User</SelectItem>
                                    <SelectItem value="amazon">Amazon User</SelectItem>
                                    <SelectItem value="amazon_admin">Amazon Admin</SelectItem>
                                  </SelectContent>
                                </Select>
                              </td>
                              <td className="py-2 px-3">
                                <Badge className={u.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                                  {u.status}
                                </Badge>
                              </td>
                              <td className="py-2 px-3">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => handleDeleteUser(u.email)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500 italic">No Amazon users registered</p>
                  )}
                </div>
                
                {/* Amazon Admins Sub-section */}
                <div className="border rounded-lg p-3 bg-orange-50/30">
                  <h4 className="font-medium text-sm text-orange-800 mb-2">Amazon Admins ({groups.amazon_admins.length})</h4>
                  {groups.amazon_admins.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-left">
                        <thead>
                          <tr className="text-xs text-slate-500 border-b">
                            <th className="py-2 px-3 font-medium">Email</th>
                            <th className="py-2 px-3 font-medium">Position</th>
                            <th className="py-2 px-3 font-medium">Site</th>
                            <th className="py-2 px-3 font-medium">Role</th>
                            <th className="py-2 px-3 font-medium">Status</th>
                            <th className="py-2 px-3 font-medium">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {groups.amazon_admins.map((u) => (
                            <tr key={u.email} className="border-b border-orange-100 hover:bg-orange-50/50">
                              <td className="py-2 px-3">
                                <p className="font-medium text-slate-900 text-sm">{u.email}</p>
                                <p className="text-xs text-slate-500">{u.first_name} {u.last_name}</p>
                              </td>
                              <td className="py-2 px-3 text-slate-600 text-sm">{u.position || '-'}</td>
                              <td className="py-2 px-3 text-slate-600 text-sm">{u.site_postcode || '-'}</td>
                              <td className="py-2 px-3">
                                <Select
                                  value={u.role}
                                  onValueChange={(role) => {
                                    const newDistributor = (role === 'zkteco' || role === 'zkteco_admin' || role === 'admin') 
                                      ? '' 
                                      : null;
                                    handleUpdateUser(u.email, role, u.status, newDistributor);
                                  }}
                                >
                                  <SelectTrigger className="w-32 h-7 text-xs">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {user?.role === 'admin' && (
                                      <>
                                        <SelectItem value="admin">Admin</SelectItem>
                                        <SelectItem value="zkteco_admin">ZKTeco Admin</SelectItem>
                                      </>
                                    )}
                                    <SelectItem value="zkteco">ZKTeco</SelectItem>
                                    <SelectItem value="distributor">Distributor</SelectItem>
                                    <SelectItem value="distributor_admin">Distributor Admin</SelectItem>
                                    <SelectItem value="subdistributor">Sub Distributor</SelectItem>
                                    <SelectItem value="installer">Installer</SelectItem>
                                    <SelectItem value="enduser">End User</SelectItem>
                                    <SelectItem value="amazon">Amazon User</SelectItem>
                                    <SelectItem value="amazon_admin">Amazon Admin</SelectItem>
                                  </SelectContent>
                                </Select>
                              </td>
                              <td className="py-2 px-3">
                                <Badge className={u.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                                  {u.status}
                                </Badge>
                              </td>
                              <td className="py-2 px-3">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => handleDeleteUser(u.email)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500 italic">No Amazon admins registered</p>
                  )}
                </div>
              </CardContent>
            )}
          </Card>
        )}

        {/* Unassigned Users */}
        {!isDistributorAdmin && (
          <UserSection
            title="Unassigned Users"
            icon={Users}
            users={groups.unassigned}
            sectionId="unassigned"
            color="orange"
          />
        )}

        {/* Empty State */}
        {users.length === 0 && pendingUsers.length === 0 && (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Users className="h-12 w-12 text-slate-300 mb-4" />
              <p className="text-slate-500">No users found</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;
